import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { tan6 } from '../../utils/utils';
import CreatorsTestimonialItem from './CreatorsTestimonialItem';
import { GatsbyImage, getImage, getImageData } from 'gatsby-plugin-image';
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from 'react-responsive-carousel';
import ChevronDownIcon from '../icons/ChevronDownIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      backgroundColor: theme.palette.primary.main,
      transform: 'skewY(-6.733deg)',
      marginTop: `calc((100vw * ${tan6} * -1) / 2)`,
      marginBottom: '32px',
      padding: `calc((100vw * ${tan6} * -1) / 2) 0`,
      borderRadius: '25px 50px 25px 50px',
      [theme.breakpoints.up(1170)]: {
        transform: 'skewY(6.733deg)',
        marginBottom: `calc((1170px * ${tan6} * -1) / 2)`,
        marginTop: `calc((1170px * ${tan6} * -1) / 2)`,
        padding: `calc((1170px * ${tan6} * -1) / 2) 0`,
        borderRadius: '50px 25px 50px 25px',
      },
    },
    content: {
      transform: 'skewY(6.733deg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '64px 24px 46px',
      maxWidth: '600px',
      margin: '0 auto',
      width: '100%',
      overflow: 'hidden',
      [theme.breakpoints.up(1170)]: {
        transform: 'skewY(-6.733deg)',
        maxWidth: 'unset',
      },
    },
    carousel: {
      position: 'relative',
      marginBottom: '24px',
      width: '100%',
      maxWidth: '1034px',
      [theme.breakpoints.up(1200)]: {
        overflow: 'visible',
        '& .carousel.carousel-slider': {
          overflow: 'visible',
        },
        '& .carousel .slider-wrapper': {
          overflow: 'visible',
        },
        '& .slide': {
          opacity: '0.5',
          transition: 'opacity 350ms ease-in-out',
        },
        '& .slide.selected': {
          opacity: '1',
        },
      },
    },
    indicators: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '16px',
      marginBottom: '32px',
    },
    indicator: {
      width: '12px',
      height: '12px',
      borderRadius: '100%',
      backgroundColor: '#FFF',
      opacity: '0.2',
    },
    controls: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: '16px',
      [theme.breakpoints.up(1200)]: {
        display: 'none',
      },
    },
    control: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      width: '54px',
      height: '54px',
      border: '2px solid #FFF',
      borderRadius: '0px 8px 8px 8px',
      background: 'none',
      boxShadow: '0px 8.59375px 34.375px rgba(0, 0, 0, 0.05)',
      cursor: 'pointer',
      backgroundColor: theme.palette.primary.main,
    },
    controlIconLeft: {
      width: '26px',
      height: '26px',
      color: '#FFF',
      transform: 'rotate(90deg)',
    },
    desktopControlPrev: {
      display: 'none',
      position: 'absolute',
      top: '0',
      left: '24px',
      height: '100%',
      flexDirection: 'column',
      justifyContent: 'center',
      zIndex: '999',
      [theme.breakpoints.up(1200)]: {
        display: 'flex',
      },
    },
    desktopControlNext: {
      display: 'none',
      position: 'absolute',
      top: '0',
      right: '24px',
      height: '100%',
      flexDirection: 'column',
      justifyContent: 'center',
      zIndex: '999',
      [theme.breakpoints.up(1200)]: {
        display: 'flex',
      },
    },
    awardsImage: {
      display: 'none',
      [theme.breakpoints.up(1200)]: {
        display: 'block',
        marginTop: '72px',
      }
    }
  }
})

const CreatorsTestimonialSection = ({
  testimonials,
  awardsImage,
}) => {
  const classes = useStyles();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const image = getImage(awardsImage);
  return (
    <section className={classes.root}>
      <div className={classes.content}>
        <Carousel
          className={classes.carousel}
          showArrows={true}
          showStatus={false}
          showThumbs={false}
          showIndicators={false}
          infiniteLoop={true}
          selectedItem={selectedIndex}
          renderArrowNext={() => {
            return (
              <div className={classes.desktopControlNext}>
                <button className={classes.control} style={{ transform: 'scaleX(-1)' }} onClick={() => { setSelectedIndex((index) => index + 1); }}>
                  <ChevronDownIcon className={classes.controlIconLeft} fill="#FFF" />
                </button>
              </div>
            );
          }}
          renderArrowPrev={() => {
            return (
              <div className={classes.desktopControlPrev}>
                <button className={classes.control} onClick={() => { setSelectedIndex((index) => index - 1); }}>
                  <ChevronDownIcon className={classes.controlIconLeft} fill="#FFF" />
                </button>
              </div>
            )
          }}
          onChange={(index) => {
            setSelectedIndex(index);
          }}
        >
          {testimonials.map((testimonial) => {
            const avatar = getImage(testimonial.creator_testimonial_avatar);
            return (
              <CreatorsTestimonialItem
                avatar={avatar}
                text={testimonial.creator_testimonial_text.text}
                author={testimonial.creator_testimonial_author.text}
                categories={testimonial.creator_testimonial_categories.text.split(', ')}
                uptimeLink={testimonial.uptime_link.url}
                twitterLink={testimonial.twitter_link.url}
                linkedinLink={testimonial.linkedin_link.url}
                youtubeLink={testimonial.youtube_link.url}
                instagramLink={testimonial.instagram_link.url}
                hackLink={testimonial.hack_link.url}
              />
            );
          })}
        </Carousel>
        <div className={classes.indicators}>
          {testimonials.map((_, index) => {
            return (
              <span
                key={`indicator-${index}`}
                className={classes.indicator}
                style={{
                  opacity: selectedIndex === index ? '1' : '0.2',
                }}
              />
            );
          })}
        </div>
        <div className={classes.controls}>
          <button className={classes.control} onClick={() => { setSelectedIndex((index) => index - 1); }}>
            <ChevronDownIcon className={classes.controlIconLeft} fill="#FFF" />
          </button>
          <button className={classes.control} style={{ transform: 'scaleX(-1)' }} onClick={() => { setSelectedIndex((index) => index + 1); }}>
            <ChevronDownIcon className={classes.controlIconLeft} fill="#FFF" />
          </button>
        </div>
        <GatsbyImage className={classes.awardsImage} image={image} alt="" />
      </div>
    </section>
  )
}

export default CreatorsTestimonialSection;
